// File: client/src/variants/zombie/useZombieRuntime.js
//
// Zombie Chess runtime. Captured pieces rise as zombies controlled by the capturer
// after 3 of the capturer's turns. Zombies move one square any direction, can't
// promote/castle, and are worth nothing. Runs through the variant contract; the only
// engine touch is the shared custom-piece registry (registerCustomPiece), used here
// to teach the engine the 'z'/'Z' zombie so check/checkmate stays correct.

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Skull } from 'lucide-react';
import { registerCustomPiece } from '../../utils/chessLogic';
import {
  zombiePieceDef, ZOMBIE_WHITE, ZOMBIE_BLACK, isZombie,
  queueRevival, advanceRevivals, REVIVE_DELAY,
} from './zombieLogic';

// Register the zombie piece with the engine exactly once (module load is fine — it's
// idempotent and namespaced to 'z', which classic chess never uses).
registerCustomPiece(ZOMBIE_WHITE, zombiePieceDef);
registerCustomPiece(ZOMBIE_BLACK, zombiePieceDef);

export function useZombieRuntime({ resumePayload }) {
  // Revival queue: [{ color, turnsLeft, square }]. Persisted for resume.
  const queueRef = useRef(resumePayload ? resumePayload.queue || [] : []);
  const pendingCaptureRef = useRef(null); // {piece, square} stashed in beforeMove
  const [display, setDisplay] = useState({ pending: (resumePayload?.queue || []).length });

  useEffect(() => {
    setDisplay({ pending: queueRef.current.length });
  }, []);

  const moveMiddleware = {
    // Record any piece being captured BEFORE the move is applied (we need the
    // pre-move board, which beforeMove receives).
    beforeMove(gameState, from, to) {
      const target = gameState.board[to.row] && gameState.board[to.row][to.col];
      pendingCaptureRef.current =
        target && target !== '' ? { piece: target, square: { row: to.row, col: to.col } } : null;
      return { allow: true };
    },

    afterMove(newState, from, to) {
      const moverColor = newState.turn; // side that just moved (turn not yet flipped)

      // 1. If this move captured something, queue it to revive for the mover.
      const cap = pendingCaptureRef.current;
      pendingCaptureRef.current = null;
      if (cap) {
        queueRef.current = queueRevival(queueRef.current, cap.piece, moverColor, cap.square);
      }

      // 2. Advance the mover's revivals; spawn any that are due onto the board.
      const { queue, spawned } = advanceRevivals(queueRef.current, newState.board, moverColor);
      queueRef.current = queue;
      if (spawned.length > 0) {
        // Reflect spawns in the committed board (already mutated) — nothing else to do;
        // getGameStatus will see them because it reads newState.board.
      }

      setDisplay({ pending: queueRef.current.length });
      return { turnEnded: true };
    },
  };

  const getPersistencePayload = useCallback(() => ({ queue: queueRef.current }), []);

  const renderOverlay = (slot) => {
    if (slot !== 'top') return null;
    const pending = display.pending;
    return (
      <div className="flex flex-wrap gap-2 items-center justify-center my-2">
        <span className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border bg-green-800/40 border-green-600/50 text-green-200">
          <Skull size={14} /> Infection: {pending} rising ({REVIVE_DELAY}-turn delay)
        </span>
        {pending > 0 && (
          <span className="text-[11px] text-slate-400">Captured pieces return as your zombies 🧟</span>
        )}
      </div>
    );
  };

  return { moveMiddleware, getPersistencePayload, renderOverlay };
}

// Re-export for tests / board rendering.
export { isZombie, ZOMBIE_WHITE, ZOMBIE_BLACK };
